import { c as _c } from "react/compiler-runtime";
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Separator } from '@/components/ui/Separator';
import { Sheet, SheetContent } from '@/components/ui/Sheet';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip';
import { GUARD_ROUTES } from '@/guards/config/guard-config';
import { useI18n } from '@/context/I18nProvider';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { PanelLeft } from '@/components/ui/icons/Icons';
import { Slot } from '@radix-ui/react-slot';
import { VariantProps, cva } from 'class-variance-authority';
import { usePathname } from 'next/navigation';
import { CSSProperties, ComponentProps, ComponentRef, createContext, forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
const SIDEBAR_COOKIE_NAME = 'sidebar:state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = '16rem';
const SIDEBAR_WIDTH_MOBILE = '18rem';
const SIDEBAR_WIDTH_ICON = '3rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';
const SIDEBAR_AUTO_COLLAPSED_BREAKPOINT = 960;
type SidebarContext = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: (triggerSource?: 'manual') => void;
  isAutoCollapsed: boolean;
};
const SidebarContext = createContext<SidebarContext | null>(null);
const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
};
const SidebarProvider = forwardRef<HTMLDivElement, ComponentProps<'div'> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}>((t0, ref) => {
  const $ = _c(50);
  let children;
  let className;
  let openProp;
  let props;
  let setOpenProp;
  let style;
  let t1;
  if ($[0] !== t0) {
    ({
      defaultOpen: t1,
      open: openProp,
      onOpenChange: setOpenProp,
      className,
      style,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = openProp;
    $[4] = props;
    $[5] = setOpenProp;
    $[6] = style;
    $[7] = t1;
  } else {
    children = $[1];
    className = $[2];
    openProp = $[3];
    props = $[4];
    setOpenProp = $[5];
    style = $[6];
    t1 = $[7];
  }
  const defaultOpen = t1 === undefined ? true : t1;
  const hasExpandedOnce = useRef(false);
  const {
    isMobile
  } = useIsMobile();
  const [openMobile, setOpenMobile] = useState(false);
  const [isManuallyToggled, setIsManuallyToggled] = useState(false);
  const [_open, _setOpen] = useState(defaultOpen);
  const [isAutoCollapsed, setIsAutoCollapsed] = useState(window.innerWidth <= SIDEBAR_AUTO_COLLAPSED_BREAKPOINT);
  const open = openProp ?? _open;
  let t2;
  if ($[8] !== open || $[9] !== setOpenProp) {
    t2 = value => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    };
    $[8] = open;
    $[9] = setOpenProp;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const setOpen = t2;
  let t3;
  if ($[11] !== isMobile || $[12] !== setOpen) {
    t3 = triggerSource => {
      if (triggerSource === "manual") {
        setIsManuallyToggled(true);
      }
      if (isMobile) {
        setOpenMobile(_temp);
        return;
      }
      setOpen(_temp2);
    };
    $[11] = isMobile;
    $[12] = setOpen;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const toggleSidebar = t3;
  let t4;
  let t5;
  if ($[14] !== isManuallyToggled) {
    t4 = () => {
      if (isManuallyToggled) {
        const timeout = setTimeout(() => {
          setIsManuallyToggled(false);
        }, 300);
        return () => clearTimeout(timeout);
      }
    };
    t5 = [isManuallyToggled];
    $[14] = isManuallyToggled;
    $[15] = t4;
    $[16] = t5;
  } else {
    t4 = $[15];
    t5 = $[16];
  }
  useEffect(t4, t5);
  const state = open ? "expanded" : "collapsed";
  let t6;
  let t7;
  if ($[17] !== isAutoCollapsed || $[18] !== toggleSidebar) {
    t6 = () => {
      const handleKeyDown = event => {
        if (isAutoCollapsed) {
          return;
        }
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleSidebar();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t7 = [isAutoCollapsed, toggleSidebar];
    $[17] = isAutoCollapsed;
    $[18] = toggleSidebar;
    $[19] = t6;
    $[20] = t7;
  } else {
    t6 = $[19];
    t7 = $[20];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const handleResize = () => {
        setIsAutoCollapsed(window.innerWidth <= SIDEBAR_AUTO_COLLAPSED_BREAKPOINT);
      };
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    };
    t9 = [];
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[23] !== isAutoCollapsed || $[24] !== isManuallyToggled || $[25] !== state || $[26] !== toggleSidebar) {
    t10 = () => {
      if (isManuallyToggled) {
        hasExpandedOnce.current = true;
        return;
      }
      const handleResize_0 = () => {
        if (isAutoCollapsed && state === "expanded") {
          toggleSidebar();
          hasExpandedOnce.current = false;
        } else {
          if (!isAutoCollapsed && state === "collapsed" && !hasExpandedOnce.current) {
            toggleSidebar();
            hasExpandedOnce.current = true;
          }
        }
      };
      handleResize_0();
      window.addEventListener("resize", handleResize_0);
      return () => window.removeEventListener("resize", handleResize_0);
    };
    t11 = [isAutoCollapsed, isManuallyToggled, state, toggleSidebar];
    $[23] = isAutoCollapsed;
    $[24] = isManuallyToggled;
    $[25] = state;
    $[26] = toggleSidebar;
    $[27] = t10;
    $[28] = t11;
  } else {
    t10 = $[27];
    t11 = $[28];
  }
  useEffect(t10, t11);
  let t12;
  if ($[29] !== isAutoCollapsed || $[30] !== isMobile || $[31] !== open || $[32] !== openMobile || $[33] !== setOpen || $[34] !== state || $[35] !== toggleSidebar) {
    t12 = {
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      isAutoCollapsed
    };
    $[29] = isAutoCollapsed;
    $[30] = isMobile;
    $[31] = open;
    $[32] = openMobile;
    $[33] = setOpen;
    $[34] = state;
    $[35] = toggleSidebar;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const contextValue = t12;
  let t13;
  if ($[37] !== style) {
    t13 = {
      "--sidebar-width": SIDEBAR_WIDTH,
      "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
      ...style
    };
    $[37] = style;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const t14 = t13 as CSSProperties;
  let t15;
  if ($[39] !== className) {
    t15 = cn("group/sidebar-wrapper flex h-full w-full has-[[data-variant=inset]]:bg-sidebar", className);
    $[39] = className;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== children || $[42] !== props || $[43] !== ref || $[44] !== t14 || $[45] !== t15) {
    t16 = <TooltipProvider delayDuration={0}><div style={t14} className={t15} ref={ref} {...props}>{children}</div></TooltipProvider>;
    $[41] = children;
    $[42] = props;
    $[43] = ref;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== contextValue || $[48] !== t16) {
    t17 = <SidebarContext.Provider value={contextValue}>{t16}</SidebarContext.Provider>;
    $[47] = contextValue;
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  return t17;
});
SidebarProvider.displayName = 'SidebarProvider';
const Sidebar = forwardRef<HTMLDivElement, ComponentProps<'div'> & {
  side?: 'left' | 'right';
  variant?: 'sidebar' | 'floating' | 'inset';
  collapsible?: 'offcanvas' | 'icon' | 'none';
}>((t0, ref) => {
  const $ = _c(47);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      side: t1,
      variant: t2,
      collapsible: t3,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  const side = t1 === undefined ? "left" : t1;
  const variant = t2 === undefined ? "sidebar" : t2;
  const collapsible = t3 === undefined ? "offcanvas" : t3;
  const pathname = usePathname();
  const {
    isMobile,
    state,
    openMobile,
    setOpenMobile
  } = useSidebar();
  if (pathname === GUARD_ROUTES.organizations.create) {
    return null;
  }
  if (collapsible === "none") {
    let t4;
    if ($[7] !== className) {
      t4 = cn("flex h-full w-[--sidebar-width] flex-col bg-sidebar text-sidebar-foreground", className);
      $[7] = className;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== children || $[10] !== props || $[11] !== ref || $[12] !== t4) {
      t5 = <div className={t4} ref={ref} {...props}>{children}</div>;
      $[9] = children;
      $[10] = props;
      $[11] = ref;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  if (isMobile) {
    let t4;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        "--sidebar-width": SIDEBAR_WIDTH_MOBILE
      };
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== children) {
      t5 = <div className="flex h-full w-full flex-col">{children}</div>;
      $[15] = children;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== side || $[18] !== t5) {
      t6 = <SheetContent data-sidebar="sidebar" data-mobile="true" className="w-[--sidebar-width] bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden" style={t4 as CSSProperties} side={side}>{t5}</SheetContent>;
      $[17] = side;
      $[18] = t5;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== openMobile || $[21] !== props || $[22] !== setOpenMobile || $[23] !== t6) {
      t7 = <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>{t6}</Sheet>;
      $[20] = openMobile;
      $[21] = props;
      $[22] = setOpenMobile;
      $[23] = t6;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    return t7;
  }
  const t4 = state === "collapsed" ? collapsible : "";
  const t5 = variant === "floating" || variant === "inset" ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]" : "group-data-[collapsible=icon]:w-[--sidebar-width-icon]";
  let t6;
  if ($[25] !== t5) {
    t6 = cn("duration-200 relative h-full w-[--sidebar-width] bg-transparent transition-[width] ease-linear", "group-data-[collapsible=offcanvas]:w-0", "group-data-[side=right]:rotate-180", t5);
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== t6) {
    t7 = <div className={t6} />;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const t8 = side === "left" ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]" : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]";
  const t9 = variant === "floating" || variant === "inset" ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]" : "group-data-[collapsible=icon]:w-[--sidebar-width-icon] group-data-[side=left]:border-r group-data-[side=right]:border-l";
  let t10;
  if ($[29] !== className || $[30] !== t8 || $[31] !== t9) {
    t10 = cn("duration-200 absolute top-0 bottom-0 z-10 h-full w-[--sidebar-width] transition-[left,right,width] ease-linear flex", t8, t9, className);
    $[29] = className;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== children) {
    t11 = <div data-sidebar="sidebar" className="flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow">{children}</div>;
    $[33] = children;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== props || $[36] !== t10 || $[37] !== t11) {
    t12 = <div className={t10} {...props}>{t11}</div>;
    $[35] = props;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== ref || $[40] !== side || $[41] !== state || $[42] !== t12 || $[43] !== t4 || $[44] !== t7 || $[45] !== variant) {
    t13 = <div ref={ref} className="group peer block text-sidebar-foreground" data-state={state} data-collapsible={t4} data-variant={variant} data-side={side}>{t7}{t12}</div>;
    $[39] = ref;
    $[40] = side;
    $[41] = state;
    $[42] = t12;
    $[43] = t4;
    $[44] = t7;
    $[45] = variant;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  return t13;
});
Sidebar.displayName = 'Sidebar';
const SidebarTrigger = forwardRef<ComponentRef<typeof Button>, ComponentProps<typeof Button>>((t0, ref) => {
  const $ = _c(20);
  let className;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onClick;
    $[3] = props;
  } else {
    className = $[1];
    onClick = $[2];
    props = $[3];
  }
  const {
    toggleSidebar,
    isAutoCollapsed
  } = useSidebar();
  const {
    t
  } = useI18n();
  if (isAutoCollapsed) {
    return null;
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("size-7", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== onClick || $[7] !== toggleSidebar) {
    t2 = event => {
      onClick?.(event);
      toggleSidebar("manual");
    };
    $[6] = onClick;
    $[7] = toggleSidebar;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <PanelLeft />;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t) {
    t4 = t("ui.toggleSidebar");
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <span className="sr-only">{t4}</span>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== props || $[15] !== ref || $[16] !== t1 || $[17] !== t2 || $[18] !== t5) {
    t6 = <Button ref={ref} data-sidebar="trigger" variant="ghost" size="icon" className={t1} onClick={t2} {...props}>{t3}{t5}</Button>;
    $[14] = props;
    $[15] = ref;
    $[16] = t1;
    $[17] = t2;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
});
SidebarTrigger.displayName = 'SidebarTrigger';
const SidebarRail = forwardRef<HTMLButtonElement, ComponentProps<'button'>>((t0, ref) => {
  const $ = _c(18);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    toggleSidebar
  } = useSidebar();
  const {
    t
  } = useI18n();
  let t1;
  if ($[3] !== t) {
    t1 = t("ui.toggleSidebar");
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== toggleSidebar) {
    t2 = () => toggleSidebar();
    $[5] = toggleSidebar;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t) {
    t3 = t("ui.toggleSidebar");
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== className) {
    t4 = cn("absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex", "[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize", "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize", "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full group-data-[collapsible=offcanvas]:hover:bg-sidebar", "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2", "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2", className);
    $[9] = className;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== props || $[12] !== ref || $[13] !== t1 || $[14] !== t2 || $[15] !== t3 || $[16] !== t4) {
    t5 = <button ref={ref} data-sidebar="rail" aria-label={t1} tabIndex={-1} onClick={t2} title={t3} className={t4} {...props} />;
    $[11] = props;
    $[12] = ref;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
});
SidebarRail.displayName = 'SidebarRail';
const SidebarInset = forwardRef<HTMLDivElement, ComponentProps<'main'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("relative flex min-h-full flex-1 flex-col bg-background", "peer-data-[variant=inset]:min-h-[calc(100svh-theme(spacing.4))] peer-data-[variant=inset]:m-2 peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 peer-data-[variant=inset]:ml-0 peer-data-[variant=inset]:rounded-xl peer-data-[variant=inset]:shadow", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <main ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarInset.displayName = 'SidebarInset';
const SidebarInput = forwardRef<ComponentRef<typeof Input>, ComponentProps<typeof Input>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("h-8 w-full bg-background shadow-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <Input ref={ref} data-sidebar="input" className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarInput.displayName = 'SidebarInput';
const SidebarHeader = forwardRef<HTMLDivElement, ComponentProps<'div'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-2 p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div ref={ref} data-sidebar="header" className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarHeader.displayName = 'SidebarHeader';
const SidebarFooter = forwardRef<HTMLDivElement, ComponentProps<'div'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex min-h-0 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div ref={ref} data-sidebar="footer" className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarFooter.displayName = 'SidebarFooter';
const SidebarSeparator = forwardRef<ComponentRef<typeof Separator>, ComponentProps<typeof Separator>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("mx-2 w-auto bg-sidebar-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <Separator ref={ref} data-sidebar="separator" className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarSeparator.displayName = 'SidebarSeparator';
const SidebarContent = forwardRef<HTMLDivElement, ComponentProps<'div'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div ref={ref} data-sidebar="content" className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarContent.displayName = 'SidebarContent';
const SidebarGroup = forwardRef<HTMLDivElement, ComponentProps<'div'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("relative flex w-full min-w-0 flex-col p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div ref={ref} data-sidebar="group" className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarGroup.displayName = 'SidebarGroup';
const SidebarGroupLabel = forwardRef<HTMLDivElement, ComponentProps<'div'> & {
  asChild?: boolean;
}>((t0, ref) => {
  const $ = _c(11);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot : "div";
  let t2;
  if ($[4] !== className) {
    t2 = cn("duration-200 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 outline-none transition-[margin,opa] ease-linear [&>svg]:size-4 [&>svg]:shrink-0", "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== Comp || $[7] !== props || $[8] !== ref || $[9] !== t2) {
    t3 = <Comp ref={ref} data-sidebar="group-label" className={t2} {...props} />;
    $[6] = Comp;
    $[7] = props;
    $[8] = ref;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});
SidebarGroupLabel.displayName = 'SidebarGroupLabel';
const SidebarGroupAction = forwardRef<HTMLButtonElement, ComponentProps<'button'> & {
  asChild?: boolean;
}>((t0, ref) => {
  const $ = _c(11);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot : "button";
  let t2;
  if ($[4] !== className) {
    t2 = cn("absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0", "after:absolute after:-inset-2 after:hidden", "group-data-[collapsible=icon]:hidden", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== Comp || $[7] !== props || $[8] !== ref || $[9] !== t2) {
    t3 = <Comp ref={ref} data-sidebar="group-action" className={t2} {...props} />;
    $[6] = Comp;
    $[7] = props;
    $[8] = ref;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});
SidebarGroupAction.displayName = 'SidebarGroupAction';
const SidebarGroupContent = forwardRef<HTMLDivElement, ComponentProps<'div'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("w-full text-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div ref={ref} data-sidebar="group-content" className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarGroupContent.displayName = 'SidebarGroupContent';
const SidebarMenu = forwardRef<HTMLUListElement, ComponentProps<'ul'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex w-full min-w-0 flex-col gap-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <ul ref={ref} data-sidebar="menu" className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarMenu.displayName = 'SidebarMenu';
const SidebarMenuItem = forwardRef<HTMLLIElement, ComponentProps<'li'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group/menu-item relative", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <li ref={ref} data-sidebar="menu-item" className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarMenuItem.displayName = 'SidebarMenuItem';
const sidebarMenuButtonVariants = cva('peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0', {
  variants: {
    variant: {
      default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
      outline: 'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]'
    },
    size: {
      default: 'h-8 text-sm',
      sm: 'h-7 text-xs',
      lg: 'h-12 text-sm group-data-[collapsible=icon]:!p-0'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});
const SidebarMenuButton = forwardRef<HTMLButtonElement, ComponentProps<'button'> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>>((t0, ref) => {
  const $ = _c(29);
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let tooltip;
  if ($[0] !== t0) {
    ({
      asChild: t1,
      isActive: t2,
      variant: t3,
      size: t4,
      tooltip,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = tooltip;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    tooltip = $[7];
  }
  const asChild = t1 === undefined ? false : t1;
  const isActive = t2 === undefined ? false : t2;
  const variant = t3 === undefined ? "default" : t3;
  const size = t4 === undefined ? "default" : t4;
  const Comp = asChild ? Slot : "button";
  const {
    isMobile,
    state
  } = useSidebar();
  let t5;
  if ($[8] !== className || $[9] !== size || $[10] !== variant) {
    t5 = cn(sidebarMenuButtonVariants({
      variant,
      size
    }), className);
    $[8] = className;
    $[9] = size;
    $[10] = variant;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== Comp || $[13] !== isActive || $[14] !== props || $[15] !== ref || $[16] !== size || $[17] !== t5) {
    t6 = <Comp ref={ref} data-sidebar="menu-button" data-size={size} data-active={isActive} className={t5} {...props} />;
    $[12] = Comp;
    $[13] = isActive;
    $[14] = props;
    $[15] = ref;
    $[16] = size;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const button = t6;
  if (!tooltip) {
    return button;
  }
  if (typeof tooltip === "string") {
    let t7;
    if ($[19] !== tooltip) {
      t7 = {
        children: tooltip
      };
      $[19] = tooltip;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    tooltip = t7;
  }
  let t7;
  if ($[21] !== button) {
    t7 = <TooltipTrigger asChild={true}>{button}</TooltipTrigger>;
    $[21] = button;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const t8 = state !== "collapsed" || isMobile;
  let t9;
  if ($[23] !== t8 || $[24] !== tooltip) {
    t9 = <TooltipContent side="right" align="center" hidden={t8} {...tooltip} />;
    $[23] = t8;
    $[24] = tooltip;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t7 || $[27] !== t9) {
    t10 = <Tooltip>{t7}{t9}</Tooltip>;
    $[26] = t7;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
});
SidebarMenuButton.displayName = 'SidebarMenuButton';
const SidebarMenuAction = forwardRef<HTMLButtonElement, ComponentProps<'button'> & {
  asChild?: boolean;
  showOnHover?: boolean;
}>((t0, ref) => {
  const $ = _c(13);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      asChild: t1,
      showOnHover: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const asChild = t1 === undefined ? false : t1;
  const showOnHover = t2 === undefined ? false : t2;
  const Comp = asChild ? Slot : "button";
  const t3 = showOnHover && "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground opacity-0";
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cn("absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0", "after:absolute after:-inset-2 after:hidden", "peer-data-[size=sm]/menu-button:top-1", "peer-data-[size=default]/menu-button:top-1.5", "peer-data-[size=lg]/menu-button:top-2.5", "group-data-[collapsible=icon]:hidden", t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== Comp || $[9] !== props || $[10] !== ref || $[11] !== t4) {
    t5 = <Comp ref={ref} data-sidebar="menu-action" className={t4} {...props} />;
    $[8] = Comp;
    $[9] = props;
    $[10] = ref;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
});
SidebarMenuAction.displayName = 'SidebarMenuAction';
const SidebarMenuBadge = forwardRef<HTMLDivElement, ComponentProps<'div'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground select-none pointer-events-none", "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground", "peer-data-[size=sm]/menu-button:top-1", "peer-data-[size=default]/menu-button:top-1.5", "peer-data-[size=lg]/menu-button:top-2.5", "group-data-[collapsible=icon]:hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div ref={ref} data-sidebar="menu-badge" className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarMenuBadge.displayName = 'SidebarMenuBadge';
const SidebarMenuSkeleton = forwardRef<HTMLDivElement, ComponentProps<'div'> & {
  showIcon?: boolean;
}>((t0, ref) => {
  const $ = _c(15);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      showIcon: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const showIcon = t1 === undefined ? false : t1;
  const width = `${Math.floor(Math.random() * 40) + 50}%`;
  let t2;
  if ($[4] !== className) {
    t2 = cn("rounded-md h-8 flex gap-2 px-2 items-center", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== showIcon) {
    t3 = showIcon && <Skeleton className="size-4 rounded-md" data-sidebar="menu-skeleton-icon" />;
    $[6] = showIcon;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      "--skeleton-width": width
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Skeleton className="h-4 flex-1 max-w-[--skeleton-width]" data-sidebar="menu-skeleton-text" style={t4 as CSSProperties} />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== props || $[11] !== ref || $[12] !== t2 || $[13] !== t3) {
    t6 = <div ref={ref} data-sidebar="menu-skeleton" className={t2} {...props}>{t3}{t5}</div>;
    $[10] = props;
    $[11] = ref;
    $[12] = t2;
    $[13] = t3;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
});
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton';
const SidebarMenuSub = forwardRef<HTMLUListElement, ComponentProps<'ul'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5", "group-data-[collapsible=icon]:hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <ul ref={ref} data-sidebar="menu-sub" className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarMenuSub.displayName = 'SidebarMenuSub';
const SidebarMenuSubItem = forwardRef<HTMLLIElement, ComponentProps<'li'>>((t0, ref) => {
  const $ = _c(5);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props || $[3] !== ref) {
    t1 = <li ref={ref} {...props} />;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
});
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';
const SidebarMenuSubButton = forwardRef<HTMLAnchorElement, ComponentProps<'a'> & {
  asChild?: boolean;
  size?: 'sm' | 'md';
  isActive?: boolean;
}>((t0, ref) => {
  const $ = _c(17);
  let className;
  let isActive;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      asChild: t1,
      size: t2,
      isActive,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = isActive;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    isActive = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const asChild = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "md" : t2;
  const Comp = asChild ? Slot : "a";
  const t3 = size === "sm" && "text-xs";
  const t4 = size === "md" && "text-sm";
  let t5;
  if ($[6] !== className || $[7] !== t3 || $[8] !== t4) {
    t5 = cn("flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground outline-none hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground", "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground", t3, t4, "group-data-[collapsible=icon]:hidden", className);
    $[6] = className;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== Comp || $[11] !== isActive || $[12] !== props || $[13] !== ref || $[14] !== size || $[15] !== t5) {
    t6 = <Comp ref={ref} data-sidebar="menu-sub-button" data-size={size} data-active={isActive} className={t5} {...props} />;
    $[10] = Comp;
    $[11] = isActive;
    $[12] = props;
    $[13] = ref;
    $[14] = size;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
});
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';
export { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupAction, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInput, SidebarInset, SidebarMenu, SidebarMenuAction, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, SidebarProvider, SidebarRail, SidebarSeparator, SidebarTrigger, useSidebar };
function _temp(open_0) {
  return !open_0;
}
function _temp2(open_1) {
  return !open_1;
}